import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useDimensions } from "./useDimensions";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import useIsMobile from "./useIsMobile";

gsap.registerPlugin(ScrollTrigger);


const OccupationStacked = ({ data }) => {
    
    const svgRef = useRef();

    const containerRef = useRef(null);
    const dimensions = useDimensions(containerRef);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!data || data.length === 0) return;

        const parsedData = data.map(d => ({
            year: new Date(d.date).getFullYear(),
            occupation: d.occupation
        }));

        const groupedData = d3.rollup(
            parsedData,
            v => v.length,
            d => d.year,
            d => d.occupation
        );

        const years = Array.from(groupedData.keys()).sort();
        if (!years.includes(2006)) {
            years.splice(years.findIndex(y => y > 2006), 0, 2006);
        }

        const occupations = Array.from(new Set(parsedData.map(d => d.occupation)));

        const occupationOrder = [
            "无业", "农民", "个体户", "学生", "临时工",  "医护人员",  "公务员",  "专业人士", "司机", "工人", "教职工", "退休", "不详"
        ];

        const sortedOccupations = occupations.sort((a, b) => {
            return occupationOrder.indexOf(a) - occupationOrder.indexOf(b);
        });

        const stackedData = years.map(year => {
            const obj = { year };
            sortedOccupations.forEach(occ => {
                obj[occ] = groupedData.get(year)?.get(occ) || 0;
            });
            return obj;
        });

        const { width, height } = dimensions;
        const margin = { top: 50, right: 10, bottom: 50, left: 30 };

        d3.select(svgRef.current).selectAll('*').remove();

        const xScale = d3.scaleBand()
            .domain(years)
            .range([margin.left, width - margin.right])
            .padding(0.65);


        const yScale = d3.scaleLinear()
            .domain([0, d3.max(stackedData, d => d3.sum(sortedOccupations, occ => d[occ]))])
            .nice()
            .range([height - margin.bottom, margin.top]);

        const colors = [
            "#CBB7B7", "#FF754C", "#BA388F", "#FFE97D", "#F6AA27", "#EF1561",  "#734F99",  "#28519D", "#2C80B6", "#7AB1E0", "#865A56", "#FBE3E3", "#5F5858"
        ];

        const colorScale = d3.scaleOrdinal(colors).domain(sortedOccupations);

        const stack = d3.stack().keys(sortedOccupations.reverse());
        const series = stack(stackedData);

        // console.log("Stacked Occupations Order:", series.map(d => d.key));  // Log the order

        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height);

        svg.selectAll("*").remove();

        svg.append("g")
              .attr("class", "svg-grid")
              .attr("transform", `translate(${margin.left}, 0)`)
              .call(
                d3.axisLeft(yScale)
                .tickSize(-width + margin.left + margin.right)
                .tickFormat("")
              )
              .select(".domain")
              .attr("stroke", "none");

        svg.append("text")
            .attr("class", "axes")
            .attr("y", 28)
            .attr("x", 8)
            .text("职业数量");

        
        const Tooltip = d3.select("body")
            .append("div")
            .attr("class", "tooltip")
            .style("pointer-events", "none")
            .style("opacity", 0);

        const handleMouseOver = (event, d) => {
            Tooltip.style("opacity", 1);
            d3.select(event.currentTarget.parentNode)
                .raise()
                .attr("stroke", "#fff")
                .attr("stroke-width", 2)
                .attr("stroke-opacity", 0.3);
            d3.select(event.currentTarget)
                .attr("stroke", "#fff")
                .attr("stroke-width", 1)
                .attr("stroke-opacity", 1);
        };

        const handleMouseMove = (event, d, occupation) => {

            Tooltip
                .html(`
                    <span class="tooltip__num">${d.data.year}</span>年</br>
                    袭击者职业：<b>${occupation}</b>（<span class="tooltip__num">${d.data[occupation]}</span>人）
                    `)
            .style("left", (event.pageX + 10) + "px")
            .style("top", (event.pageY - 85) + "px");
        };

        const handleMouseOut = (event) => {
            Tooltip.style("opacity", 0);
            const occupation = d3.select(event.currentTarget.parentNode).datum().key;
            d3.select(event.currentTarget.parentNode)
                .attr("stroke", colorScale(occupation))
                .attr("stroke-width", 1)
                .attr("stroke-opacity", 1);
            d3.select(event.currentTarget)
                .attr("stroke", colorScale(occupation))
                .attr("stroke-width", 1)
                .attr("stroke-opacity", 1);
        };

        svg.append("g")
            .selectAll("g")
            .data(series)
            .enter().append("g")
            .attr("fill", d => colorScale(d.key))
            .attr("fill-opacity", 0.8)
            .attr("stroke", d => colorScale(d.key) )
            .attr("stroke-opacity", 1)
            // .attr("class", d => `occupation-group ${["农民", "无业", "个体户"].includes(d.key) ? "highlight-group" : ""}`)
            .selectAll("rect")
            .data(d => d)
            .enter().append("rect")
            .attr("x", d => xScale(d.data.year))
            .attr("y", d => Math.min(yScale(d[0]), yScale(d[1])))
            .attr("height", d => Math.abs(yScale(d[0]) - yScale(d[1])))
            .attr("width", xScale.bandwidth())
            .on("mouseover", handleMouseOver)
            // .on("mousemove", handleMouseMove)
            .on("mousemove", (event, d) => handleMouseMove(event, d, d3.select(event.currentTarget.parentNode).datum().key))
            .on("mouseout", handleMouseOut);

        svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(d3.axisBottom(xScale)
                // .tickFormat(d3.format("d"))
                // .ticks(5)
                .tickValues([2000, 2005, 2010, 2015, 2020])
                .tickPadding(10)
                .tickSize(0)
                );

        svg.append("g")
            .attr("class", "y-axis")
            .attr("transform", `translate(${margin.left},0)`)
            .call(d3.axisLeft(yScale)
                .tickSize(0)
                .tickPadding(10))
            .select(".domain")
            .attr("opacity", 0);

        // gsap.fromTo(
        //     ".highlight-group rect",
        //     { 
        //         // opacity: 1
        //         strokeWidth: 1
        //      },
        //     {
        //         // opacity: 0.5,
        //         strokeWidth: 3,
        //         duration: 1.5,
        //         scrollTrigger: {
        //             trigger: containerRef.current,
        //             start: "+=250 center",
        //             end: "center center",
        //             scrub: true,
        //             markers:true,
        //         }
        //     }
        // );

        // gsap.to(
        //     ".highlight-group rect",
        //     {
        //         opacity: 1,
        //         duration: 1.5,
        //         scrollTrigger: {
        //             trigger: containerRef.current,
        //             start: "center center",
        //             end: "bottom center",
        //             scrub: true,
        //         }
        //     }
        // );


            // Stack order  无业, 农民, 个体户 
            const topBars = svg.selectAll("rect")
                .filter(function () {
                    return d3.select(this.parentNode).datum()?.key === "无业";
                });
    
            const bottomBars = svg.selectAll("rect")
                .filter(function () {
                    return d3.select(this.parentNode).datum()?.key === "个体户";
                });
    
            if (topBars.empty() || bottomBars.empty()) return;
    
            // Extract positions
            const topPositions = topBars.nodes().map(node => ({
                x: node.x.baseVal.value,
                y: node.y.baseVal.value
            }));
    
            const bottomPositions = bottomBars.nodes().map(node => ({
                x: node.x.baseVal.value,
                y: node.y.baseVal.value + node.height.baseVal.value // bottom of "无业"
            }));
            
            const calloutText = svg.append("foreignObject")
                .attr("x", isMobile ? xScale(2010) + 5 : xScale(2012))
                .attr("y", isMobile ? yScale(14) : yScale(13))
                .attr("class", "callout")
                .attr("width", isMobile ? 88 : 120)
                .attr("height", isMobile ? 80 : 60)
                .attr("opacity", 1)
                .append("xhtml:div")
                .html("中国无差别袭击事件的嫌疑人多为农民、无业人员和个体户");

            const calloutLine = svg.append("line")
                .attr("x1", xScale(2014) + 4)
                .attr("y1", yScale(11) - 4)
                .attr("x2", xScale(2014) + 4)
                .attr("y2", yScale(8) - 4)
                .attr("stroke", "yellow")
                .attr("stroke-width", 0.5)
                .attr("opacity", 1);
    
            // Group by x position (year-based)
            const yearGroups = d3.groups([...topPositions, ...bottomPositions], d => d.x);
    
            yearGroups.forEach(([x, positions]) => {

                const yMin = d3.min(positions, d => d.y + 2); // Highest (top of 个体户)
                const yMax = d3.max(positions, d => d.y - 2); // Lowest (bottom of 无业)
                const barWidth = topBars.nodes()[0].width.baseVal.value;
    
                const highlightRect = svg.append("rect")
                    .attr("x", x - 2)
                    .attr("y", yMin - 4)
                    .attr("width", barWidth + 4)
                    .attr("height", yMax - yMin + 6)
                    .attr("stroke", "yellow")
                    .attr("stroke-opacity", 0.7)
                    .attr("stroke-width", 2)
                    .attr("fill", "none")
                    .attr("opacity", 1);
                

                    // gsap.set([highlightRect.node(), calloutText.node(), calloutLine.node()], { opacity: 0 });

                    // gsap.to([highlightRect.node(), calloutText.node(), calloutLine.node()], {
                    //     opacity: 1,
                    //     duration: 0.1, // Quickly appear
                    //     scrollTrigger: {
                    //         trigger: containerRef.current,
                    //         start: "top top",
                    //         end: "bottom-=150 60%",
                    //         toggleActions: "play none none reverse",
                    //         scrub: true,
                    //         markers: true,
                    //     }
                    // });
                    
                    // gsap.to([highlightRect.node(), calloutText.node(), calloutLine.node()], {
                    //     opacity: 0,
                    //     duration: 1, // Fade out smoothly
                    //     scrollTrigger: {
                    //         trigger: containerRef.current,
                    //         start: "top-=10% 10%",
                    //         end: "bottom-=150 60%",
                    //         toggleActions: "play none none reverse",
                    //         scrub: true,
                    //         // markers: true,
                    //     }
                    // });


                    gsap.set([highlightRect.node(), calloutText.node(), calloutLine.node()], { opacity: 0 });


                    gsap.to([highlightRect.node(), calloutText.node(), calloutLine.node()], {
                        keyframes: [
                            { opacity: 1, duration: 1 }, // Quickly appear
                            { opacity: 0, duration: 0.5 }   // Fade out smoothly
                        ],
                        scrollTrigger: {
                            trigger: containerRef.current,
                            start: "top 5%",
                            end: "bottom+=280 bottom",
                            toggleActions: "play none none reverse",
                            scrub: true,
                            // markers: true,
                        }
                    });
                    

            });


    }, [data, dimensions]);

  return (
    <div className="grid-container-right" id="scatter-age-year">
        <div className="article-body">
            <div style={{ height:"120px" }}></div>
            <p className="subheader">中国城镇化进程中，</p>
            <h2>拆迁、征地可能造成巨大的被剥夺感，甚至引发血案</h2>
            <div className="spacerdiv" />

            <p>台湾学者在分析无差别杀害事件的动机时<a href="https://language.site.nthu.edu.tw/var/file/212/1212/img/4086/430652809.pdf" target="_blank">表明</a>，除了犯罪者本身的因素，更多的是社会结构体制的问题。从职业上看，在已知的数据中，中国无差别袭击事件的嫌疑人多为农民、无业人员和个体户。另外，农民工、工人、学生教职工与医护人员也较为常见。在某种层面上，这也反映了在过去二十多年来，城中村改造、企业改制以及医疗教育等社会热议的话题。</p>

            { isMobile ? `` : <div>
                <div style={{ height:"25vh" }}/>
                
                <p>值得一提的是，对比日本台湾和美国的数据，唯有中国无差别袭击事件的嫌疑人中有“农民”的分类。且该数量在2019年后有大幅减少。在很大程度上, 这与中国城镇化进程有密切关系。</p>
                <p>改革开放以来，中国城镇化进程增长迅速，增速远超其他发达<a href="http://paper.people.com.cn/rmrbhwb/html/2023-03/29/content_25972874.htm" target="_blank">国家</a>。而剧烈的社会转型带来的副产品之一，是“<a href="https://www.cas.cn/zt/jzt/ltzt/dqxdhshjjfzdbyzl/jyytz/200409/t20040906_2670821.shtml" target="_blank">强制征地</a>”，农民的权益受到极大损害。有学者认为，越是容易被剥夺的个体，越倾向诉求于显而易见或高调的行动, 制造轰动效果。歪脑发现，很多无差别袭击事件的犯罪动机都与“拆迁赔偿”有关。例如2016年曹再发开车撞人事件, 嫌疑人是有名的“钉子户”。他的房子在2012年被强拆后，因不满拆迁赔偿，曾爬上大桥，造成交通瘫痪，也曾扬言要在学校制造血案。在长期“上访”无果后，<a href="http://www.xinhuanet.com/politics/2016-09/18/c_129284989.htm" target="_blank">几年后</a>他开车撞人，造成3死5伤。此外，民间至少还有数十位农民为捍卫<a href="https://cn.nytimes.com/china/20130909/c09chinaurban/" target="_blank">土地</a>，采用<a href="https://www.amnesty.org/es/wp-content/uploads/2021/06/asa170012012zh.pdf" target="_blank">自焚</a>的方式来表达不满。媒体也把中国城市化的新浪潮称为“实力强大的国家和固执的农民之间的暴力斗争”。</p>
                <p>而这场“暴力斗争”随着中国城市化进程变慢而缓和下来，在过去的五年间，随着铁路和互联网的大面积覆盖。弗里德曼认为，中国人口增长萎缩，城市饱和，“这都意味着对土地的压力变少了，受影响的人也变少了。”弗里德曼解释，因此纠纷的焦点也从农村转移了。</p>
                <p>然而，压倒人最后一根稻草的也可能并非是尖锐的社会矛盾，而是亲密关系的破碎。《无差别杀人犯罪：一种罕见而荒谬的暴力形态》文章总结，台湾无差别嫌疑犯较多生长在多重高风险家庭，亲密关系冷淡。弗里德曼认为，社会纽带赋予人们归属感，给予人物质上或心理上的支持。但在高度流动性的现代社会，社会关系变得微弱，例如珠江三角洲，人口流动性极强，个体更容易呈现原子化的状态。孤独个体更可能走向极端。李厚辰用上访群体和烂尾楼维权群体对比，同样是社会矛盾的受害者，但结成团体，就<a href="http://hx.cnd.org/2017/10/30/%E8%8C%89%E8%8E%89%EF%BC%9A%E9%80%86%E8%A2%AD%EF%BC%8C%E4%B8%AD%E5%9B%BD%E8%AE%BF%E6%B0%91%E5%9C%A8%E7%BE%8E%E5%9B%BD%E7%BB%B4%E6%9D%83%E4%B8%80%E4%B8%80%E8%B0%88%E8%A2%81%E5%BB%BA%E6%96%8C%E7%9A%84/" target="_blank">往往</a>能表现灵活，有各种斗争策略，不容易在偏执中选择极端暴力。在无差别袭击事件中，我们也看到一些在离婚等或求爱拒绝后采取暴力行动，例如2024年年末珠江撞人事件，嫌疑人被曝光是因离婚后对财产分割不满而行凶，亲密关系的冲突是直接的诱因。</p>
            </div> }


        </div>

        <div ref={containerRef} className="container-ref desktop-sticky" style={{ height:"85vh"}}>
            <h4>中国袭击嫌疑人多为农民、城市无业人员和个体商贩</h4>
            <svg ref={svgRef}></svg>
        </div>

        { isMobile ? <div className="article-body"><div style={{ height:"5vh" }}/>
                
                <p>值得一提的是，对比日本台湾和美国的数据，唯有中国无差别袭击事件的嫌疑人中有“农民”的分类。且该数量在2020年后有大幅减少。在很大程度上, 这与中国城镇化进程有密切关系。</p>
                <p>改革开放以来，中国城镇化进程增长迅速，增速远超其他发达<a href="http://paper.people.com.cn/rmrbhwb/html/2023-03/29/content_25972874.htm" target="_blank">国家</a>。而剧烈的社会转型带来的副产品之一，是“<a href="https://www.cas.cn/zt/jzt/ltzt/dqxdhshjjfzdbyzl/jyytz/200409/t20040906_2670821.shtml" target="_blank">强制征地</a>”，农民的权益受到极大损害。有学者认为，越是容易被剥夺的个体，越倾向诉求于显而易见或高调的行动, 制造轰动效果。歪脑发现，很多无差别袭击事件的犯罪动机都与“拆迁赔偿”有关。例如2016年曹再发开车撞人事件, 嫌疑人是有名的“钉子户”。他的房子在2012年被强拆后，因不满拆迁赔偿，曾爬上大桥，造成交通瘫痪，也曾扬言要在学校制造血案。在长期“上访”无果后，<a href="http://www.xinhuanet.com/politics/2016-09/18/c_129284989.htm" target="_blank">几年后</a>他开车撞人，造成3死5伤。此外，民间至少还有数十位农民为捍卫<a href="https://cn.nytimes.com/china/20130909/c09chinaurban/" target="_blank">土地</a>，采用<a href="https://www.amnesty.org/es/wp-content/uploads/2021/06/asa170012012zh.pdf" target="_blank">自焚</a>的方式来表达不满。媒体也把中国城市化的新浪潮称为“实力强大的国家和固执的农民之间的暴力斗争”。</p>
                <p>而这场“暴力斗争”随着中国城市化进程变慢而缓和下来，在过去的五年间，随着铁路和互联网的大面积覆盖。弗里德曼认为，中国人口增长萎缩，城市饱和，“这都意味着对土地的压力变少了，受影响的人也变少了。”弗里德曼解释，因此纠纷的焦点也从农村转移了。</p>
                <p>然而，压倒人最后一根稻草的也可能并非是尖锐的社会矛盾，而是亲密关系的破碎。《无差别杀人犯罪：一种罕见而荒谬的暴力形态》文章总结，台湾无差别嫌疑犯较多生长在多重高风险家庭，亲密关系冷淡。弗里德曼认为，社会纽带赋予人们归属感，给予人物质上或心理上的支持。但在高度流动性的现代社会，社会关系变得微弱，例如珠江三角洲，人口流动性极强，个体更容易呈现原子化的状态。孤独个体更可能走向极端。李厚辰用上访群体和烂尾楼维权群体对比，同样是社会矛盾的受害者，但结成团体，就<a href="http://hx.cnd.org/2017/10/30/%E8%8C%89%E8%8E%89%EF%BC%9A%E9%80%86%E8%A2%AD%EF%BC%8C%E4%B8%AD%E5%9B%BD%E8%AE%BF%E6%B0%91%E5%9C%A8%E7%BE%8E%E5%9B%BD%E7%BB%B4%E6%9D%83%E4%B8%80%E4%B8%80%E8%B0%88%E8%A2%81%E5%BB%BA%E6%96%8C%E7%9A%84/" target="_blank">往往</a>能表现灵活，有各种斗争策略，不容易在偏执中选择极端暴力。在无差别袭击事件中，我们也看到一些在离婚等或求爱拒绝后采取暴力行动，例如2024年年末珠江撞人事件，嫌疑人被曝光是因离婚后对财产分割不满而行凶，亲密关系的冲突是直接的诱因。</p></div> : `` }
    </div>
  )
}

export default OccupationStacked;